import React, { useMemo } from 'react';

import { queryParameterContext } from './QueryParameterContext';
import { useCourseMappings, CourseMappingsSearchQuery } from '../data/useCourseMappings';
import type { CourseMappingResource } from '@ab/ams-school-api/types/private/v1';

type SearchResult = {
  courseMappings: CourseMappingResource[];
  totalCourseMappings: number;
  isFetchingCourseMappings: boolean;
  courseMappingsError?: unknown;
};

const searchResultContext = React.createContext<SearchResult | undefined>(undefined);

export function SearchResultContextProvider(props: { children: React.ReactNode }) {
  const { parameters } = queryParameterContext.useQueryParameter();
  const query = useMemo<CourseMappingsSearchQuery>(() => {
    return {
      'page[size]': parameters.pageSize,
      'page[number]': parameters.pageNumber,
      'filter[archived]': parameters.archived === 'false',
      'filter[country]': parameters.country.length > 0 ? parameters.country : undefined,
      sort: parameters.sort,
    };
  }, [parameters.pageNumber, parameters.pageSize, parameters.archived, parameters.country, parameters.sort]);

  const { courseMappings, totalCourseMappings, isFetchingCourseMappings, courseMappingsError } =
    useCourseMappings(query);
  const value = useMemo(
    () => ({
      courseMappings,
      totalCourseMappings,
      isFetchingCourseMappings,
      courseMappingsError,
    }),
    [courseMappings, totalCourseMappings, isFetchingCourseMappings, courseMappingsError]
  );

  return <searchResultContext.Provider value={value}>{props.children}</searchResultContext.Provider>;
}

export function useSearchResult() {
  const value = React.useContext(searchResultContext);
  if (value === undefined) {
    throw new Error('useSearchResult must be used within a SearchResultContextProvider');
  }
  return value;
}
